import { useUserSubscriptionStatus } from './useSubscriptions';
import { useBillingUsage } from './useBilling';
import { useAuth } from '@/contexts/AuthContext';
import { isPartnerClient } from '@/utils/partnerUtils';

/**
 * Hook to determine if user has access to premium features
 * Returns access status and reasons for restricted access
 */
export function useSubscriptionAccess() {
  const { profile } = useAuth();
  const { data: subscriptionStatus, isLoading: statusLoading } = useUserSubscriptionStatus();
  const { data: billingUsage, isLoading: billingLoading } = useBillingUsage();

  const isLoading = statusLoading || billingLoading;

  // If loading, return loading state without assuming access
  if (isLoading) {
    return {
      hasAccess: false, // Don't assume access during loading
      isLoading: true,
      restrictionReason: null,
      hasAssignedPlan: false,
      needsPayment: false,
    };
  }

  // Check for assigned plan that needs payment activation
  const hasAssignedPlan = subscriptionStatus?.has_plan_assignment && subscriptionStatus?.needs_stripe_subscription;
  
  // Check for active subscription
  const hasActiveSubscription = subscriptionStatus?.has_stripe_subscription || false;
  
  // Check for delinquent account (no credits, service suspended)
  const isDelinquent = billingUsage?.isDelinquent || false;
  
  // Check if in free trial
  const isInFreeTrial = billingUsage?.freeTrialInfo?.isInFreeTrial || false;

  // Check if user is a partner client
  const isPartnerClientUser = isPartnerClient(profile?.user_type);

  // Determine access and restriction reason
  let hasAccess = false; // Default to no access
  let restrictionReason: string | null = null;
  let needsPayment = false;

  // Partner clients with paid plans assigned by partner - no payment method needed
  if (isPartnerClientUser && subscriptionStatus?.has_plan_assignment && subscriptionStatus?.plan_requires_payment) {
    hasAccess = true;
    restrictionReason = null;
    needsPayment = false;
  } else if (subscriptionStatus?.has_plan_assignment && !subscriptionStatus?.needs_stripe_subscription) {
    // Handle case where user has plan assignment but doesn't need Stripe subscription
    // This covers test users, partners, founders, and other free plans
    hasAccess = true;
    restrictionReason = null;
    needsPayment = false;
  } else if (hasActiveSubscription) {
    hasAccess = true; // Active subscription users have access
    restrictionReason = null;
  } else if (hasAssignedPlan) {
    hasAccess = false;
    needsPayment = true;
    restrictionReason = 'assigned_plan_needs_payment';
  } else if (isDelinquent) {
    hasAccess = false;
    needsPayment = true;
    restrictionReason = 'delinquent_account';
  } else if (isInFreeTrial) {
    hasAccess = true; // Free trial users have access
    restrictionReason = null;
  } else {
    // No plan, not in trial, not delinquent - needs to subscribe
    hasAccess = false;
    needsPayment = true;
    restrictionReason = 'no_subscription';
  }

  return {
    hasAccess,
    isLoading: false,
    restrictionReason,
    hasAssignedPlan,
    hasActiveSubscription,
    needsPayment,
    isDelinquent,
    isInFreeTrial,
  };
}